<template>
  <ValidationObserver
    ref="observer"
    style="width: 100%"
  >
    <v-form
      ref="form"
      v-model="validForm"
      style="width: 100%"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-divider />
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="pb-0"
          >
            <h4 class="text-h4 text-uppercase mb-10">
              Enter your reference
            </h4>
            <p class="text-body-1 mb-4">
              Please enter your room location to be used as a reference for both
              your curtain and track order.
            </p>
            <ValidationProvider
              v-for="(location, i) in locations"
              :key="i"
              v-slot="{ errors }"
              ref="provider"
              :rules="roomLocationRules(i)"
              name="room_location"
            >
              <v-text-field
                v-model="locations[i]"
                name="room location"
                outlined
                dense
                solo
                :error-messages="errors"
              >
                <template
                  v-if="locations.length > 1"
                  #append
                >
                  <div
                    class="locations-append"
                    @click="removeLocation(i)"
                  >
                    <p>Remove</p>
                    <v-icon class="pr-4">
                      mdi mdi-trash-can-outline
                    </v-icon>
                  </div>
                </template>
              </v-text-field>
            </ValidationProvider>
            <h4 class="text-h4 text-uppercase mb-10 mt-5">
              Your order
            </h4>
            <quantity-picker
              :quantity="locations.length"
              :locations="locations"
              @addLocation="addLocation"
            />
          </v-col>
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-divider />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <p>Would you like to order more curtains?</p>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="order_variant"
            >
              <v-radio-group
                v-model="continueOrder"
                :error-messages="errors"
                name="order variants"
              >
                <v-radio
                  v-for="variant in orderVariants"
                  :key="variant.value"
                  :label="variant.label"
                  :value="variant.value"
                />
              </v-radio-group>
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <v-divider />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <fabric-picker-card />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <div class="confirmButtonsBlock">
              <v-btn
                class="primary opacity-bg"
                @click="back"
              >
                <v-icon class="white--text pr-5">
                  mdi mdi-chevron-left
                </v-icon>
                Previous step
              </v-btn>
              <submit-button
                :disabled="disabled"
                @click="submit"
              />
            </div>
          </v-col>
          <v-col
            cols="6"
            md="3"
          />
        </v-row>
      </v-container>
    </v-form>
  </ValidationObserver>
</template>

<script>
import FabricPickerCard from '@/components/Cards/FabricPickerCard'
import QuantityPicker from '@/components/Pickers/QuantityPicker'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import SubmitButton from '@/components/Buttons/SubmitButton'

export default {
  name: 'RoomLocationPicker',
  components: {
    SubmitButton,
    FabricPickerCard,
    QuantityPicker,
    ValidationObserver,
    ValidationProvider
  },
  data() {
    return {
      validForm: false,
      locations: [''],
      continueOrder: null,
      orderVariants: [
        {
          value: true,
          label: 'Yes, I want to order more curtains'
        },
        {
          value: false,
          label: 'No, I do not want to order more curtains'
        }
      ]
    }
  },
  computed: {
    disabled() {
      let isLocationsNames = false
      const isNoNames = () => {
        const names = this.locations.filter((name) => name === '')
        return names.length > 0 ? true : false
      }
      if (
        this.continueOrder === null ||
        isNoNames() ||
        this.isNotUniqueLocations()
      ) {
        return true
      } else return false
    }
  },
  watch: {
    locations(value) {
      this.$store.dispatch('order/setCurtainQuantity', value.length)
    },
    continueOrder(val) {
      this.$refs.observer.validate()
      this.$store.commit('order/submitButton/setContinueOrder', val)
    }
  },
  methods: {
    isNotUniqueLocations(id) {
      if (id) {
        let locations = Object.assign([], this.locations)
        locations.splice(id, 1)
        return locations.includes(this.location)
      } else {
        return new Set(this.locations).size !== this.locations.length
      }
    },
    roomLocationRules(id) {
      return {
        required: true,
        uniqueLocation: this.isNotUniqueLocations(id)
      }
    },
    addLocation(location) {
      this.locations.push(location)
    },
    removeLocation(index) {
      this.locations.splice(index, 1)
    },
    reset() {
      this.locations = ['']
      this.$refs.observer.reset()
      this.$refs.provider[0].reset()

      this.continueOrder = null
    },
    // addToCart curtain submitting
    async submit() {
      this.$store.commit('order/submitButton/startLoader')
      await Promise.all(
        this.locations.map(async (location) => {
          await this.$store.dispatch('order/createCurtainOrder', location)
        })
      ).then(
        () => {
          if (this.continueOrder) {
            this.$emit('addToCart')
            this.$emit('pickLocation', this.locations)
            this.$store.dispatch('order/resetState')
            this.$emit('restart')
            this.reset()
          } else {
            this.$emit('addToCart')
            this.$emit('pickLocation', this.locations)
            this.$store.commit('order/submitButton/setComplete')
            this.$store.commit('order/submitButton/setAddedToCart')
          }
          setTimeout(
            () => this.$store.commit('order/submitButton/endLoader'),
            10000
          )
        },
        (error) => {
          // handle reject here
          console.error(error)
          this.$store.commit('order/submitButton/endLoader')
        }
      )
    },
    back() {
      this.$emit('back')
    }
  }
}
</script>

<style lang="scss" scoped>
.locations-append {
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;

  p {
    margin: 0;
  }

  .v-icon {
    padding: 0 0 0 6px !important;
    font-size: 20px;
  }
}
</style>
